import * as React from 'react';
import AccountActionsInterface, { ACCOUNT_EVENT_IDS } from '@central-products/interface-account-actions';
import {
  WelcomeMessagesFlowInterface,
  StateTypes,
  StateChangePayload,
} from '@central-products/welcome-messages-flow-interface';
import MessageBusTransport from '@central-products/message-transport-bus';
import { AbstractTransport } from '@central-products/abstract-transport-bus';
import { shouldReactOnAction } from '../utils/utils';
import { AdobePayloadData } from '../typings';
import { ReactAny } from '../typings/ReactAny';
import { Props } from '../WelcomeMessagesProvider/WelcomeMessagesProviderComponent';

export default (Component: ReactAny<Props>) => ({
  transport,
  // this is needed 'cos TS compiler doesn't allow trailing commas for ...rest
  // tslint:disable-next-line trailing-comma
  ...props
}: Partial<Props> & { transport?: AbstractTransport }) => {
  const transportInstance = transport || MessageBusTransport.getInstance();
  const flowProxy = transportInstance.get<WelcomeMessagesFlowInterface>(WelcomeMessagesFlowInterface);
  const accountActionsProxy = transportInstance.get<AccountActionsInterface>(AccountActionsInterface);

  return (
    <Component
      {...props}
      onWmRequested={getActionRequestedHandler(flowProxy)}
      onWmDisplayed={getOnWmDisplayedHandler(accountActionsProxy, flowProxy)}
      onCTAClicked={getCTAHandler(accountActionsProxy)}
      onCancelRequested={getCancelHandler(flowProxy)}
      // tslint:disable-next-line jsx-no-lambda
      dismissUI={() => flowProxy.cancel()}
    />
  );
};

const getActionRequestedHandler = (flowProxy: WelcomeMessagesFlowInterface) => {
  return (cb: (action: AdobePayloadData) => void) => {
    flowProxy.onStateChanged(({ state, action }: StateChangePayload) => {
      if (state === StateTypes.UI_REQUESTED && action && shouldReactOnAction(action.actionId)) {
        // @ts-ignore
        cb(action.data);
      }
    });
  };
};

const getOnWmDisplayedHandler = (
  orchestratorProxy: AccountActionsInterface,
  flowProxy: WelcomeMessagesFlowInterface,
) => {
  return (payload: AdobePayloadData) => {
    flowProxy.uiDisplayed();
    orchestratorProxy
      .execute({
        eventId: ACCOUNT_EVENT_IDS.WELCOME_MESSAGES_CAMPAIGN_DISPLAYED,
        context: { data: payload },
      })
      .then(({ error }) => {
        if (error) {
          throw new Error('Unable to call execute for WELCOME_MESSAGES_CAMPAIGN_DISPLAYED event');
        }
      });
  };
};

const getCTAHandler = (orchestratorProxy: AccountActionsInterface) => {
  return (url: string, target: string) => {
    orchestratorProxy
      .execute({
        eventId: ACCOUNT_EVENT_IDS.WELCOME_MESSAGES_NAVIGATE_REQUESTED,
        context: { data: { url, target } },
      })
      .then(({ error }) => {
        if (error) {
          throw new Error('Unable to call execute for WELCOME_MESSAGES_NAVIGATE_REQUESTED event');
        }
      });
  };
};

const getCancelHandler = (flowProxy: WelcomeMessagesFlowInterface) => {
  return (cb: () => void) => {
    flowProxy.onStateChanged(({ state }: StateChangePayload) => {
      if (state === StateTypes.NONE) {
        cb();
      }
    });
  };
};
